// Screen 1, Map. The public destination map.
//
// WHAT "REALTIME" MEANS HERE (round 3 addendum, Brian's own words): when any
// traveller publishes a diary entry, a new pin appears on everyone's map
// immediately, with no reload. It is NOT live tracking of people moving.
// So the primary layer is DESTINATION pins, one per place that has public
// diary entries, and live presence is a secondary layer that starts off.
//
// A pin is a PLACE, not a person. Several independent travellers post diaries
// at the same destination, so tapping a pin lists everyone who kept a diary
// there, tapping a row opens THAT traveller's entry for THIS place (their
// notes and their photos), and their full timeline is one tap further.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Icon,
  IconBook,
  IconCamera,
  IconChevronRight,
  IconCrosshair,
  IconCube,
  IconGlobe,
  IconLayers,
  IconMap,
  IconPin,
  IconRoute,
} from "@traveldiary/ui";
import { TravellerAvatar } from "../components/TravellerAvatar";
import { MapView, type MapDimension, type UserFix } from "../components/MapView";
import { MomentDetail } from "../components/MomentDetail";
import { Sheet } from "../components/Sheet";
import { TravellerTimeline } from "../components/TravellerTimeline";
import { apiClient } from "../lib/api";
import type { Cluster } from "../lib/cluster";
import { INDONESIA_BOUNDS } from "../lib/config";
import { formatDistance, formatRelative } from "../lib/format";
import { groupMomentsIntoPlaces, momentsAtPlace, travellersOfPlaces } from "../lib/places";
import { navigate } from "../lib/router";
import { subscribeRealtime } from "../lib/realtime";
import { startTracker } from "../lib/tracker";
import type { Basemap } from "../lib/maplibre";
import type { AuthoredMoment, LivePresence, Place, PublicUser, TrackPoint } from "../lib/types";
import { useApp } from "../state/app";

const PRESENCE_REFRESH_MS = 30_000;

type MapSheet =
  // A marker can cover several destinations once they crowd together, so the
  // sheet picks the destination first and the traveller second.
  | { kind: "places"; places: Place[] }
  | { kind: "place"; places: Place[] }
  | { kind: "entry"; entry: AuthoredMoment; place: Place | null }
  | { kind: "timeline"; user: PublicUser }
  | { kind: "travellers"; members: LivePresence[] };

export function MapScreen() {
  const { t, locale, authUser, activeTripId, exploreMoments, upsertMoment, mapFocus } = useApp();
  const [track, setTrack] = useState<TrackPoint[]>([]);
  const [serverPlaces, setServerPlaces] = useState<Place[] | null>(null);
  const [presence, setPresence] = useState<LivePresence[]>([]);
  const [showLive, setShowLive] = useState(false);
  const [user, setUser] = useState<UserFix | null>(null);
  const [follow, setFollow] = useState(false);
  const [dimension, setDimension] = useState<MapDimension>("2d");
  const [basemap, setBasemap] = useState<Basemap>("street");
  const [recenterNonce, setRecenterNonce] = useState(0);
  const [sheet, setSheet] = useState<MapSheet | null>(null);
  const [focus, setFocus] = useState<{ lat: number; lng: number; nonce: number } | null>(null);
  const [geoDenied, setGeoDenied] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const seenTrackIds = useRef(new Set<string>());

  const appendPoints = useCallback((points: TrackPoint[]) => {
    setTrack((current) => {
      const fresh = points.filter((point) => !seenTrackIds.current.has(point.id));
      if (fresh.length === 0) return current;
      for (const point of fresh) seenTrackIds.current.add(point.id);
      return [...current, ...fresh].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
    });
  }, []);

  // Own recorded trail. A per trip feature drawn under the public pins, never
  // the public map's main content.
  useEffect(() => {
    seenTrackIds.current = new Set();
    setTrack([]);
    if (!activeTripId) return;
    let cancelled = false;
    void apiClient
      .listTrack(activeTripId)
      .then((points) => {
        if (cancelled) return;
        for (const point of points) seenTrackIds.current.add(point.id);
        setTrack(points.slice().sort((a, b) => a.recordedAt.localeCompare(b.recordedAt)));
      })
      .catch(() => {
        // Offline: the trail rebuilds from live fixes below.
      });
    return () => {
      cancelled = true;
    };
  }, [activeTripId]);

  // Destinations. The server owns the grouping; when this API build has no
  // /places route yet the same rule runs over the public feed instead, so the
  // map is never empty and the two can never disagree.
  const loadPlaces = useCallback(async () => {
    try {
      setServerPlaces(await apiClient.listPlaces(INDONESIA_BOUNDS));
    } catch {
      setServerPlaces(null);
    }
  }, []);

  useEffect(() => {
    void loadPlaces();
  }, [loadPlaces]);

  const places = useMemo<Place[]>(
    () => serverPlaces ?? groupMomentsIntoPlaces(exploreMoments),
    [serverPlaces, exploreMoments],
  );

  // Live travellers, the secondary layer. Only fetched while it is switched
  // on, so a demoted layer costs nothing when nobody asked for it.
  const loadPresence = useCallback(async () => {
    try {
      const live = await apiClient.listPresence();
      setPresence(live.filter((entry) => Number.isFinite(entry.lat) && Number.isFinite(entry.lng)));
    } catch {
      // Keep whatever is on screen, the socket may still be feeding updates.
    }
  }, []);

  useEffect(() => {
    if (!showLive) {
      setPresence([]);
      return;
    }
    void loadPresence();
    const timer = setInterval(() => void loadPresence(), PRESENCE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [showLive, loadPresence]);

  // The PUBLIC topic, no tripId. `moment.created` is what this product calls
  // realtime: a new diary entry lands as a new pin, immediately.
  useEffect(() => {
    const handle = subscribeRealtime(null, {
      onFrame(frame) {
        if (frame.type === "moment.created" || frame.type === "moment.updated") {
          upsertMoment(frame.moment);
          const place = frame.place;
          if (place) {
            setServerPlaces((current) =>
              current === null
                ? current
                : [place, ...current.filter((entry) => entry.id !== place.id)],
            );
          } else {
            void loadPlaces();
          }
          return;
        }
        if (frame.type === "moment.deleted") {
          void loadPlaces();
          return;
        }
        if (frame.type === "presence.moved") {
          const incoming = frame.presence;
          if (!incoming?.user?.id) return;
          setPresence((current) => [
            ...current.filter((entry) => entry.user.id !== incoming.user.id),
            incoming,
          ]);
          return;
        }
        if (frame.type === "presence.left") {
          setPresence((current) => current.filter((entry) => entry.user.id !== frame.userId));
        }
      },
    });
    return () => handle.close();
  }, [upsertMoment, loadPlaces]);

  // Own trip topic, for the trail.
  useEffect(() => {
    if (!activeTripId) return;
    const handle = subscribeRealtime(activeTripId, {
      onFrame(frame) {
        if (frame.type === "track.appended") appendPoints(frame.points);
      },
    });
    return () => handle.close();
  }, [activeTripId, appendPoints]);

  // One geolocation watch for the whole app: it feeds the trail AND publishes
  // this device's position for the live layer.
  useEffect(() => {
    const stop = startTracker({
      tripId: activeTripId,
      publishPresence: true,
      onFix(fix) {
        setGeoDenied(false);
        setUser({ lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM });
      },
      onError(error) {
        if (error.kind === "denied") setGeoDenied(true);
      },
    });
    return stop;
  }, [activeTripId]);

  // A focus request from Explore: fly to that destination.
  useEffect(() => {
    if (!mapFocus) return;
    setFocus({ lat: mapFocus.lat, lng: mapFocus.lng, nonce: mapFocus.nonce });
    setFollow(false);
  }, [mapFocus]);

  const walked = useMemo(() => {
    let total = 0;
    for (let i = 1; i < track.length; i += 1) {
      const previous = track[i - 1]!;
      const point = track[i]!;
      const dLat = ((point.lat - previous.lat) * Math.PI) / 180;
      const dLng = ((point.lng - previous.lng) * Math.PI) / 180;
      const lat1 = (previous.lat * Math.PI) / 180;
      const lat2 = (point.lat * Math.PI) / 180;
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
      total += 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
    }
    return total;
  }, [track]);

  const onSelectPlaces = useCallback((cluster: Cluster<Place>) => {
    const selected = cluster.members.map((member) => member.item);
    setSheet(
      selected.length > 1 ? { kind: "places", places: selected } : { kind: "place", places: selected },
    );
  }, []);

  const onSelectTravellers = useCallback((cluster: Cluster<LivePresence>) => {
    setSheet({ kind: "travellers", members: cluster.members.map((member) => member.item) });
  }, []);

  const focusTraveller = useCallback((entry: LivePresence) => {
    setFollow(false);
    setFocus((current) => ({ lat: entry.lat, lng: entry.lng, nonce: (current?.nonce ?? 0) + 1 }));
    setSheet({ kind: "timeline", user: entry.user });
  }, []);

  const sheetTitle = (() => {
    if (!sheet) return "";
    if (sheet.kind === "places") return `${t("map.destinations")} ${sheet.places.length}`;
    if (sheet.kind === "place") {
      return sheet.places.find((place) => place.placeName)?.placeName ?? t("map.placeTitle");
    }
    if (sheet.kind === "entry") return sheet.place?.placeName || t("map.placeTitle");
    if (sheet.kind === "timeline") return sheet.user.displayName;
    return `${t("map.travellersHere")} ${sheet.members.length}`;
  })();

  return (
    <div className="map-stage">
      <MapView
        basemap={basemap}
        dimension={dimension}
        places={places}
        // Open on the destinations, still inside the country lock.
        fitToData
        presence={showLive ? presence : []}
        selfUserId={authUser?.id ?? null}
        user={user}
        follow={follow}
        recenterNonce={recenterNonce}
        focus={focus}
        labels={{
          self: t("map.you"),
          travellers: t("map.travellersHere"),
          place: t("map.diariesHere"),
        }}
        onSelectPlaces={onSelectPlaces}
        onSelectTravellers={onSelectTravellers}
        onError={() => setMapFailed(true)}
      />

      <div className="map-controls">
        <button
          type="button"
          className="map-btn"
          onClick={() => setRecenterNonce((n) => n + 1)}
          aria-label={t("map.recenter")}
          title={t("map.recenter")}
        >
          <Icon size={20}>
            <IconCrosshair />
          </Icon>
        </button>
        <button
          type="button"
          className={follow ? "map-btn map-btn-on" : "map-btn"}
          aria-pressed={follow}
          onClick={() => setFollow((value) => !value)}
          aria-label={follow ? t("map.unfollow") : t("map.follow")}
          title={follow ? t("map.unfollow") : t("map.follow")}
        >
          <Icon size={20}>
            <IconPin />
          </Icon>
        </button>
        {/* Live travellers are the secondary layer and start switched off. */}
        <button
          type="button"
          className={showLive ? "map-btn map-btn-on" : "map-btn"}
          aria-pressed={showLive}
          onClick={() => setShowLive((value) => !value)}
          aria-label={showLive ? t("map.hideLive") : t("map.showLive")}
          title={showLive ? t("map.hideLive") : t("map.showLive")}
        >
          <Icon size={20}>
            <IconGlobe />
          </Icon>
        </button>
        <button
          type="button"
          className={dimension === "3d" ? "map-btn map-btn-on" : "map-btn"}
          aria-pressed={dimension === "3d"}
          onClick={() => setDimension((value) => (value === "2d" ? "3d" : "2d"))}
          aria-label={dimension === "3d" ? t("map.dimension2d") : t("map.dimension3d")}
          title={dimension === "3d" ? t("map.dimension2d") : t("map.dimension3d")}
        >
          <Icon size={20}>
            <IconCube />
          </Icon>
        </button>
        <button
          type="button"
          className={basemap === "satellite" ? "map-btn map-btn-on" : "map-btn"}
          aria-pressed={basemap === "satellite"}
          onClick={() => setBasemap((value) => (value === "street" ? "satellite" : "street"))}
          aria-label={basemap === "satellite" ? t("map.street") : t("map.satellite")}
          title={basemap === "satellite" ? t("map.street") : t("map.satellite")}
        >
          <Icon size={20}>
            <IconLayers />
          </Icon>
        </button>
      </div>

      <div className="map-readout">
        <span className="map-readout-item">
          <Icon size={14} color="var(--color-text-muted)">
            <IconMap />
          </Icon>
          {places.length}
        </span>
        <span className="map-readout-item">
          <Icon size={14} color="var(--color-text-muted)">
            <IconBook />
          </Icon>
          {exploreMoments.length}
        </span>
        {track.length > 0 ? (
          <span className="map-readout-item">
            <Icon size={14} color="var(--color-text-muted)">
              <IconRoute />
            </Icon>
            {formatDistance(walked, locale)}
          </span>
        ) : null}
      </div>

      {geoDenied || mapFailed ? (
        <p className="map-notice" role="status">
          {mapFailed ? t("map.loadFailed") : t("map.locationDenied")}
        </p>
      ) : null}

      {/*
        Capture, the map's primary action. Capture is location bound, so the
        traveller reaches it from the place they are already looking at, in
        one tap. Bottom right, sitting ABOVE MapLibre's compact attribution
        block, which is licence required and must never be covered, and clear
        of the control stack in the top right corner.
      */}
      <button
        type="button"
        className="map-capture"
        onClick={() => navigate("capture")}
        aria-label={t("capture.title")}
      >
        <Icon size={24} color="var(--color-primary-contrast)">
          <IconCamera />
        </Icon>
      </button>

      <Sheet open={Boolean(sheet)} title={sheetTitle} onClose={() => setSheet(null)}>
        {sheet?.kind === "places" ? (
          <PlaceList
            places={sheet.places}
            onOpen={(place) => setSheet({ kind: "place", places: [place] })}
          />
        ) : null}
        {sheet?.kind === "place" ? (
          <PlaceTravellers
            places={sheet.places}
            selfUserId={authUser?.id ?? null}
            onOpenEntry={(entry, place) => setSheet({ kind: "entry", entry, place })}
          />
        ) : null}
        {sheet?.kind === "entry" ? (
          <MomentDetail
            entry={sheet.entry}
            onBack={() => setSheet(sheet.place ? { kind: "place", places: [sheet.place] } : null)}
            onOpenTimeline={(author) => setSheet({ kind: "timeline", user: author })}
          />
        ) : null}
        {sheet?.kind === "timeline" ? <TravellerTimeline user={sheet.user} /> : null}
        {sheet?.kind === "travellers" ? (
          <LiveTravellers
            members={sheet.members}
            selfUserId={authUser?.id ?? null}
            onOpen={focusTraveller}
          />
        ) : null}
      </Sheet>
    </div>
  );
}

/** Several destinations under one marker: pick the destination first. */
function PlaceList({ places, onOpen }: { places: Place[]; onOpen(place: Place): void }) {
  const { t, locale } = useApp();
  const ordered = [...places].sort((a, b) => b.lastMomentAt.localeCompare(a.lastMomentAt));
  return (
    <ul className="who-list">
      {ordered.map((place) => (
        <li key={place.id}>
          <button type="button" className="who-row" onClick={() => onOpen(place)}>
            <span className="who-mark">
              <Icon size={20}>
                <IconMap />
              </Icon>
            </span>
            <span className="who-text">
              <span className="who-name">{place.placeName ?? t("map.placeTitle")}</span>
              <span className="who-meta">
                {t("map.travellerCount")} {place.travellerCount}
                {" "}
                {formatRelative(place.lastMomentAt, locale)}
              </span>
            </span>
            <Icon size={18} color="var(--color-text-muted)">
              <IconChevronRight />
            </Icon>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Who kept a diary at this destination. One row per traveller, newest first,
 * and the row opens that traveller's own entry for this place.
 */
function PlaceTravellers({
  places,
  selfUserId,
  onOpenEntry,
}: {
  places: Place[];
  selfUserId: string | null;
  onOpenEntry(entry: AuthoredMoment, place: Place): void;
}) {
  const { t, locale, exploreMoments } = useApp();
  const [entries, setEntries] = useState<AuthoredMoment[] | null>(null);
  const single = places.length === 1 ? places[0]! : null;

  useEffect(() => {
    if (!single) {
      setEntries(null);
      return;
    }
    let cancelled = false;
    void apiClient
      .placeEntries(single.id)
      .then((payload) => {
        if (!cancelled && Array.isArray(payload?.entries)) setEntries(payload.entries);
      })
      .catch(() => {
        // No /places route on this API build. The local grouping stands.
      });
    return () => {
      cancelled = true;
    };
  }, [single]);

  const rows = useMemo(() => {
    const oneRowPerTraveller = (list: AuthoredMoment[], place: Place) => {
      const seen = new Set<string>();
      return list
        .filter((entry) => {
          const id = entry.author?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .map((entry) => ({ entry, place }));
    };
    if (single) {
      return oneRowPerTraveller(entries ?? momentsAtPlace(exploreMoments, single.id), single);
    }
    // A marker that covers several nearby destinations lists them all.
    return places.flatMap((place) =>
      oneRowPerTraveller(momentsAtPlace(exploreMoments, place.id), place),
    );
  }, [single, entries, places, exploreMoments]);

  return (
    <div className="who-wrap">
      <p className="who-lede">
        {t("map.travellerCount")} {travellersOfPlaces(places).total}
      </p>
      <ul className="who-list">
        {rows.map(({ entry, place }) => (
          <li key={`${place.id}:${entry.author?.id ?? entry.id}`}>
            <button type="button" className="who-row" onClick={() => onOpenEntry(entry, place)}>
              <TravellerAvatar
                user={entry.author}
                isSelf={entry.author?.id === selfUserId}
                size="md"
              />
              <span className="who-text">
                <span className="who-name">{entry.author?.displayName ?? t("explore.someone")}</span>
                <span className="who-meta">
                  {/* The sheet title already names a single destination, so
                      only a multi destination marker repeats it per row. */}
                  {!single && place.placeName ? `${place.placeName} ` : ""}
                  {formatRelative(entry.capturedAt, locale)}
                </span>
              </span>
              <Icon size={18} color="var(--color-text-muted)">
                <IconChevronRight />
              </Icon>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Who is standing here right now, the secondary live layer. */
function LiveTravellers({
  members,
  selfUserId,
  onOpen,
}: {
  members: LivePresence[];
  selfUserId: string | null;
  onOpen(entry: LivePresence): void;
}) {
  const { t, locale } = useApp();
  const ordered = [...members].sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt));
  return (
    <ul className="who-list">
      {ordered.map((entry) => (
        <li key={entry.user.id}>
          <button type="button" className="who-row" onClick={() => onOpen(entry)}>
            <TravellerAvatar
              user={entry.user}
              isSelf={entry.user.id === selfUserId}
              size="md"
            />
            <span className="who-text">
              <span className="who-name">
                {entry.user.displayName}
                {entry.user.id === selfUserId ? ` ${t("map.youSuffix")}` : ""}
              </span>
              <span className="who-meta">{formatRelative(entry.lastSeenAt, locale)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
